import React from 'react';
import { 
  Search, 
  Globe, 
  Mail, 
  Linkedin, 
  ExternalLink,
  Heart,
  Settings
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Reopen Google Funding Choices (the site's CMP) so visitors can change or
  // withdraw consent. No-op outside the EEA where the CMP isn't loaded.
  const manageCookies = () => {
    if (typeof window === 'undefined' || !window.googlefc) return;
    const show = () =>
      window.googlefc.showRevocationMessage && window.googlefc.showRevocationMessage();
    if (window.googlefc.showRevocationMessage) show();
    else if (window.googlefc.callbackQueue) window.googlefc.callbackQueue.push(show);
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-max px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-center md:text-left">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-2 mb-4 justify-center md:justify-start">
                <div className="w-10 h-10 brand-gradient rounded-lg flex items-center justify-center">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xl font-bold">Free SERP Checker</p>
                  <p className="text-sm text-gray-400">Google Search Results Tool</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 max-w-md mx-auto md:mx-0">
                A free tool to see Google's live local results from any country — no
                VPN, no proxy, no signup. Built for SEO work and market research.
              </p>
              <div className="flex space-x-4 justify-center md:justify-start">
                <a 
                  href="https://danielpajuelo.com/sobre-mi/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Visit Daniel Pajuelo's website"
                >
                  <Globe className="w-5 h-5" />
                </a>
                <a 
                  href="https://www.linkedin.com/in/daniel-pajuelo/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Connect on LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a 
                  href="mailto:contact@freeserpchecker.org" 
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Send email"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="text-center md:text-left">
              <h2 className="text-lg font-semibold mb-4">Quick Links</h2>
                              <ul className="space-y-2 text-center md:text-left">
                  <li>
                    <button 
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="text-gray-400 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-0"
                    >
                      SERP Checker Tool
                    </button>
                  </li>
                  <li>
                    <a
                      href="/google-location-changer/"
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Google Location Changer
                    </a>
                  </li>
                                  <li>
                    <a 
                      href="/features/" 
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Features
                    </a>
                  </li>
                  <li>
                    <a 
                      href="/how-to-use/" 
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      How to Use
                    </a>
                  </li>
                  <li>
                    <a 
                      href="/faq/" 
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      FAQ
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://danielpajuelo.com/sobre-mi/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors flex items-center space-x-1 justify-center md:justify-start"
                    >
                      <span>About Creator</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
              </ul>
            </div>

          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-800 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 text-center md:text-left">
            <div className="flex items-center space-x-2 text-gray-400 justify-center md:justify-start">
              {/* The year is computed again on hydration: a build from last year must not
                  fail hydration on 1 January (A06). React patches the text instead. */}
              <span suppressHydrationWarning>© {currentYear} Free SERP Checker. Made with</span>
              <Heart className="w-4 h-4 text-red-500" />
              <span>by</span>
              <a 
                href="https://danielpajuelo.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
              >
                Daniel Pajuelo SEO
              </a>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-400 justify-center md:justify-start">
              <a 
                href="/privacy" 
                className="hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="/terms" 
                className="hover:text-white transition-colors"
              >
                Terms of Service
              </a>
              <a 
                href="/cookies" 
                className="hover:text-white transition-colors"
              >
                Cookies Policy
              </a>
              <button
                type="button"
                onClick={manageCookies}
                className="hover:text-white transition-colors flex items-center space-x-1 bg-transparent border-none cursor-pointer p-0 text-sm text-gray-400"
              >
                <Settings className="w-4 h-4" />
                <span>Manage Cookies</span>
              </button>
            </div>
          </div>
        </div>


      </div>
    </footer>
  );
};

export default Footer; 